import React, { useState, useEffect } from "react";
import "./ProductPopup.css";
import { backend_url, currency } from "../../App";
import binIcon from "../Assets/bin.png";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import DownloadButton from "../MediaField/DownloadButton";
import { downloadFile } from "../../utils/media";
import { canDownloadMedia } from "../../utils/roleUtils";

const statusColor = (s) => {
  if (!s) return "pp-status-default";
  if (s.toLowerCase() === "in-stock")    return "pp-status-in";
  if (s.toLowerCase() === "low stock")   return "pp-status-low";
  if (s.toLowerCase() === "out of stock") return "pp-status-out";
  return "pp-status-default";
};

const InfoCard = ({ label, value }) => (
  <div className="pp-info-card">
    <span className="pp-info-label">{label}</span>
    <span className="pp-info-value">{value || "—"}</span>
  </div>
);

const ProductPopup = ({ product, onClose }) => {
  const [activeTab, setActiveTab] = useState("details");
  const [reviews, setReviews]     = useState([]);
  const [loadingReviews, setLoadingReviews] = useState(false);
  const [activeImg, setActiveImg] = useState(0);
  const [downloadingAll, setDownloadingAll] = useState(false);

  useEffect(() => {
    if (activeTab === "reviews" && product?.id) fetchReviews(product.id);
  }, [activeTab, product?.id]);

  useEffect(() => setActiveImg(0), [product?.id]);

  const fetchReviews = (productId) => {
    setLoadingReviews(true);
    fetch(`${backend_url}/api/customerreview?productId=${productId}`)
      .then((r) => r.json())
      .then((data) => setReviews(data.filter((r) => String(r.productId) === String(productId))))
      .catch((e) => console.error(e))
      .finally(() => setLoadingReviews(false));
  };

  const handleDeleteReview = (reviewId) => {
    if (!window.confirm("Delete this review?")) return;
    fetch(`${backend_url}/api/customerreview/${reviewId}`, { method: "DELETE" })
      .then(() => {
        toast.success("Review deleted");
        setReviews((p) => p.filter((r) => r._id !== reviewId));
      })
      .catch(() => toast.error("Failed to delete review"));
  };

  const handleToggleShowHide = (review) => {
    const action = review.showHide ? "hide" : "show";
    if (!window.confirm(`${action.charAt(0).toUpperCase() + action.slice(1)} this review?`)) return;
    fetch(`${backend_url}/api/customerreview/${review._id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ showHide: !review.showHide }),
    })
      .then((r) => { if (!r.ok) throw new Error(); toast.success(`Review ${action}d`);
        setReviews((p) => p.map((r) => r._id === review._id ? { ...r, showHide: !r.showHide } : r));
      })
      .catch(() => toast.error(`Failed to ${action} review`));
  };

  if (!product) return null;

  const imageUrls = (product.images || [])
    .filter(Boolean)
    .map((img) => (img.startsWith("http") ? img : `${backend_url}${img}`));
  const imgSrc = imageUrls[activeImg] || imageUrls[0] || null;

  const handleDownloadAll = async () => {
    setDownloadingAll(true);
    for (const url of imageUrls) {
      await downloadFile(url);
    }
    setDownloadingAll(false);
  };

  const offerPrice = product.old_price && product.new_price
    ? (product.old_price - (product.old_price * product.new_price) / 100).toFixed(2)
    : null;

  return (
    <>
      <div className="pp-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
        <div className="pp-modal">

          {/* ── Close ── */}
          <button className="pp-close" onClick={onClose}>✕</button>

          {/* ── Tabs ── */}
          <div className="pp-tabs">
            <button className={`pp-tab ${activeTab === "details" ? "active" : ""}`}
              onClick={() => setActiveTab("details")}>Product Details</button>
            <button className={`pp-tab ${activeTab === "reviews" ? "active" : ""}`}
              onClick={() => setActiveTab("reviews")}>
              Reviews {reviews.length > 0 && <span className="pp-tab-badge">{reviews.length}</span>}
            </button>
          </div>

          <div className="pp-body">
            {/* ── Left — Image Panel ── */}
            <div className="pp-left">
              <div className="pp-img-wrap">
                {imgSrc
                  ? <img src={imgSrc} alt={product.name} className="pp-img" onError={(e) => { e.target.style.display = "none"; }} />
                  : <div className="pp-img-placeholder">No Image</div>
                }
              </div>
              {imageUrls.length > 1 && (
                <div className="pp-thumbs">
                  {imageUrls.map((url, i) => (
                    <button
                      key={url}
                      type="button"
                      className={`pp-thumb ${url === imgSrc ? "active" : ""}`}
                      onClick={() => setActiveImg(i)}
                      title={`Image ${i + 1}`}
                    >
                      <img src={url} alt={`${product.name} ${i + 1}`} />
                    </button>
                  ))}
                </div>
              )}

              {imgSrc && canDownloadMedia() && (
                <div className="pp-download-row">
                  <DownloadButton
                    url={imgSrc}
                    label={imageUrls.length > 1 ? `Download ${imageUrls.indexOf(imgSrc) + 1}/${imageUrls.length}` : "Download Image"}
                  />
                  {imageUrls.length > 1 && (
                    <button
                      type="button"
                      className="download-btn"
                      onClick={handleDownloadAll}
                      disabled={downloadingAll}
                    >
                      <span aria-hidden="true">⬇</span> {downloadingAll ? "Downloading..." : `Download All (${imageUrls.length})`}
                    </button>
                  )}
                </div>
              )}

              {/* Stock ID chip */}
              <div className="pp-id-chip">
                <span className="pp-id-label">Stock No</span>
                <span className="pp-id-val">{product.unique_id}</span>
              </div>

              {/* Status badge */}
              <div className={`pp-status-badge ${statusColor(product.status)}`}>
                <span className="pp-status-dot" />
                {product.status || "Unknown"}
              </div>

              {/* Best seller */}
              {product.bestseller === "Yes" || product.bestseller === true ? (
                <div className="pp-bestseller-badge">⭐ Best Seller</div>
              ) : null}
            </div>

            {/* ── Right — Content ── */}
            <div className="pp-right">

              {/* Name + Category pills */}
              <div className="pp-name-row">
                <h2 className="pp-name">{product.name}</h2>
                <div className="pp-cat-pills">
                  {product.category && <span className="pp-pill pp-pill-cat">{product.category}</span>}
                  {product.subcategory && <span className="pp-pill pp-pill-sub">{product.subcategory}</span>}
                </div>
              </div>

              {/* Pricing */}
              <div className="pp-pricing">
                {product.old_price && (
                  <span className="pp-old-price">{currency}{product.old_price}</span>
                )}
                {offerPrice && <span className="pp-offer-price">{currency}{offerPrice}</span>}
                {product.new_price > 0 && (
                  <span className="pp-discount-badge">{product.new_price}% OFF</span>
                )}
              </div>

              {/* Description */}
              {product.description && (
                <p className="pp-desc">{product.description}</p>
              )}

              {/* ── Details Tab ── */}
              {activeTab === "details" && (
                <>
                  <div className="pp-info-grid">
                    <InfoCard label="Material"    value={product.material} />
                    <InfoCard label="Weight"      value={product.weight} />
                    <InfoCard label="Size"        value={product.size} />
                    <InfoCard label="Color"       value={product.color} />
                    <InfoCard label="Quality"     value={product.quality} />
                    <InfoCard label="Scale"       value={product.scale} />
                    <InfoCard label="Quantity"    value={product.quantity} />
                  </div>

                  {(product.note || product.disclaimer || product.washcare) && (
                    <div className="pp-extra-info">
                      {product.note       && <p><strong>Note:</strong> {product.note}</p>}
                      {product.disclaimer && <p><strong>Disclaimer:</strong> {product.disclaimer}</p>}
                      {product.washcare   && <p><strong>Wash Care:</strong> {product.washcare}</p>}
                    </div>
                  )}
                </>
              )}

              {/* ── Reviews Tab ── */}
              {activeTab === "reviews" && (
                <div className="pp-reviews">
                  {loadingReviews ? (
                    <p className="pp-no-reviews">Loading reviews…</p>
                  ) : reviews.length === 0 ? (
                    <p className="pp-no-reviews">No reviews yet.</p>
                  ) : (
                    <div className="pp-reviews-grid">
                      {reviews.map((review, i) => (
                        <div key={i} className="pp-review-card">
                          <div className="pp-review-top">
                            <div className="pp-review-avatar">
                              {review.name?.[0]?.toUpperCase() || "?"}
                            </div>
                            <div className="pp-review-meta">
                              <span className="pp-review-name">{review.name}</span>
                              <span className="pp-review-email">{review.email}</span>
                              <div className="pp-stars">
                                {[1,2,3,4,5].map((s) => (
                                  <span key={s} className={s <= review.rating ? "pp-star on" : "pp-star"}>★</span>
                                ))}
                                <span className="pp-rating-num">{review.rating}/5</span>
                              </div>
                            </div>
                            <button className="pp-del-btn" onClick={() => handleDeleteReview(review._id)}>
                              <img src={binIcon} alt="delete" />
                            </button>
                          </div>
                          {review.reviewTitle && <h4 className="pp-review-title">{review.reviewTitle}</h4>}
                          <p className="pp-review-text">{review.review}</p>
                          {review.reviewImage && (
                            <img src={review.reviewImage} alt="review" className="pp-review-img"
                              onError={(e) => { e.target.style.display = "none"; }} />
                          )}
                          <div className="pp-review-footer">
                            <button className={`pp-toggle-btn ${review.showHide ? "shown" : "hidden"}`}
                              onClick={() => handleToggleShowHide(review)}>
                              {review.showHide ? "👁 Visible" : "🙈 Hidden"}
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

            </div>
          </div>
        </div>
      </div>
      <ToastContainer />
    </>
  );
};

export default ProductPopup;
